import React from "react";
import { Nav, NavItem } from "react-bootstrap";
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import MultiBackend, { TouchTransition } from "react-dnd-multi-backend";
import TouchBackend from "react-dnd-touch-backend";
import FontAwesome from "react-fontawesome";
import DayView from "./DayView";
import Dispatcher from "./Dispatcher";
import {
  CrewModal,
  DeleteEventModal,
  DeliveryModal,
  EditEventModal,
  LoadReportModal,
  PrintWorkOrderModal,
  SettingsModal,
} from "./modals";
import MonthView from "./MonthView";
import styles from "./Scheduler.css";
import SchedulerNavbar from "./SchedulerNavbar";
import WeekView from "./WeekView";
import EquipmentWeekView from "./EquipmentWeekView";
import Sidebar from "./AvailableEvents/AvailableEmployees/Sidebar";
import SidebarJobs from "./AvailableEvents/AvailableJobs/SidebarJobs";
import AppointmentCalendarModal from "./modals/AppointmentCalendarModal";

const CustomHTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true }, // Note that you can call your backends with options
      preview: false,
      transition: TouchTransition,
    },
  ],
};

const componentSelector = (mode, selectedWeekView) => {
  switch (mode) {
    case "day":
      return DayView;
    case "week":
      if (selectedWeekView === "equipment") return EquipmentWeekView;
      else return WeekView;
    case "month":
      return MonthView;
    case "dispatch":
      return Dispatcher;
    default:
      return DayView;
  }
};

const Scheduler = ({
  entity,
  schedulerView,
  mode,
  onBidsTabClick,
  onDeliveriesTabClick,
  onJobsTabClick,
  onAppointmentsTabClick,
  onToggleBids,
  onToggleDeliveries,
  onToggleJobs,
  showBids,
  onToggleAppointments,
  showAppointments,
  showDeliveries,
  showJobs,
  showSearch,
  showEmployees,
  setEmployeeToggle,
  setJobToggle,
  jobSearchToggle,
  employeeSearchToggle,
}) => {
  const Component = componentSelector(mode, schedulerView);

  if (mode === "week" && schedulerView === "equipment") {
    if (entity === "appointments") {
      return (
        <DndProvider backend={MultiBackend} options={CustomHTML5toTouch}>
          <div className={styles.container}>
            <SchedulerNavbar />
            <div className={styles.content}>
              {showEmployees && (
                <div className={styles.search} style={{ width: "fit-content" }}>
                  <Sidebar
                    employeeToggle={employeeSearchToggle}
                    setEmployeeToggle={setEmployeeToggle}
                    entity={entity}
                  />
                </div>
              )}

              <div className={styles.calendar}>
                <Nav bsStyle="pills" className={styles.tabs}>
                  <NavItem active={entity === "jobs"} onClick={onJobsTabClick}>
                    <FontAwesome
                      name={showJobs ? "check-square-o" : "square-o"}
                      onClick={onToggleJobs}
                    />{" "}
                    Jobs
                  </NavItem>

                  <NavItem
                    active={entity === "appointments"}
                    onClick={onAppointmentsTabClick}
                  >
                    <FontAwesome
                      name={showAppointments ? "check-square-o" : "square-o"}
                      onClick={onToggleAppointments}
                    />{" "}
                    Appointments
                  </NavItem>
                </Nav>

                <Component className={styles.component} />
              </div>
              {showSearch && (
                <div
                  className={styles.search}
                  style={{ maxWidth: "fit-content" }}
                >
                  <SidebarJobs
                    jobToggle={jobSearchToggle}
                    setJobToggle={setJobToggle}
                  />
                </div>
              )}
            </div>
            <CrewModal />
            <DeleteEventModal />
            <EditEventModal />
            <DeliveryModal />
            <LoadReportModal />
            <PrintWorkOrderModal />
            <SettingsModal />
            <AppointmentCalendarModal />
          </div>
        </DndProvider>
      );
    } else {
      return (
        <DndProvider backend={MultiBackend} options={CustomHTML5toTouch}>
          <div className={styles.container}>
            <SchedulerNavbar />
            <div className={styles.content}>
              {showEmployees && (
                <div className={styles.search} style={{ width: "fit-content" }}>
                  <Sidebar
                    employeeToggle={employeeSearchToggle}
                    setEmployeeToggle={setEmployeeToggle}
                  />
                </div>
              )}

              <div className={styles.calendar}>
                <Nav bsStyle="pills" className={styles.tabs}>
                  <NavItem active={entity === "jobs"} onClick={onJobsTabClick}>
                    <FontAwesome
                      name={showJobs ? "check-square-o" : "square-o"}
                      onClick={onToggleJobs}
                    />{" "}
                    Jobs
                  </NavItem>

                  <NavItem
                    active={entity === "appointments"}
                    onClick={onAppointmentsTabClick}
                  >
                    <FontAwesome
                      name={showAppointments ? "check-square-o" : "square-o"}
                      onClick={onToggleAppointments}
                    />{" "}
                    Appointments
                  </NavItem>
                </Nav>

                <Component className={styles.component} />
              </div>
              {showSearch && (
                <div
                  className={styles.search}
                  style={{ maxWidth: "fit-content" }}
                >
                  <SidebarJobs
                    jobToggle={jobSearchToggle}
                    setJobToggle={setJobToggle}
                  />
                </div>
              )}
            </div>
            <CrewModal />
            <DeleteEventModal />
            <EditEventModal />
            <DeliveryModal />
            <LoadReportModal />
            <PrintWorkOrderModal />
            <SettingsModal />
            <AppointmentCalendarModal />
          </div>
        </DndProvider>
      );
    }
  } else {
    return (
      <DndProvider backend={MultiBackend} options={CustomHTML5toTouch}>
        <div className={styles.container}>
          <SchedulerNavbar />
          <div className={styles.content}>
            <div className={styles.calendar}>
              <Nav bsStyle="pills" className={styles.tabs}>
                <NavItem active={entity === "jobs"} onClick={onJobsTabClick}>
                  <FontAwesome
                    name={showJobs ? "check-square-o" : "square-o"}
                    onClick={onToggleJobs}
                  />{" "}
                  Jobs
                </NavItem>
                <NavItem
                  active={entity === "purchaseOrders"}
                  onClick={onDeliveriesTabClick}
                >
                  <FontAwesome
                    name={showDeliveries ? "check-square-o" : "square-o"}
                    onClick={onToggleDeliveries}
                  />{" "}
                  Deliveries
                </NavItem>
                <NavItem active={entity === "bids"} onClick={onBidsTabClick}>
                  <FontAwesome
                    name={showBids ? "check-square-o" : "square-o"}
                    onClick={onToggleBids}
                  />{" "}
                  Bids
                </NavItem>
                <NavItem
                  active={entity === "appointments"}
                  onClick={onAppointmentsTabClick}
                >
                  <FontAwesome
                    name={showAppointments ? "check-square-o" : "square-o"}
                    onClick={onToggleAppointments}
                  />{" "}
                  Appointments
                </NavItem>
              </Nav>
              <Component className={styles.component} />
            </div>
            {showSearch && (
              <div
                className={styles.search}
                style={{ maxWidth: "fit-content" }}
              >
                <SidebarJobs
                  jobToggle={jobSearchToggle}
                  setJobToggle={setJobToggle}
                />
              </div>
            )}
          </div>
          <CrewModal />
          <DeleteEventModal />
          <EditEventModal />
          <DeliveryModal />
          <LoadReportModal />
          <PrintWorkOrderModal />
          <SettingsModal />
          <AppointmentCalendarModal />
        </div>
      </DndProvider>
    );
  }
};

export default Scheduler;
